import { Listing } from '@app/models'

import {
  REPLY_SELECT_SORT_LABEL_MAP,
  REPLY_SELECT_TRANSACTION_LABEL_MAP,
} from './constants'
import { getListingAdvertiserType } from './helpers'
import { trackGTMEvent } from './trackGTMEvent'
import {
  ChatFilterEventType,
  ConversationActorRole,
  ConversationViewDetails,
  ReplySelectEntityDetails,
  ReplySelectModalOpenedDetails,
  ReplySelectSearchDetails,
  ReplySelectSortingDetails,
  ReplySelectTransactionTypeDetails,
} from './types'

export const trackConversationView = (
  conversationId: number,
  listing: Listing | null,
  details: ConversationViewDetails | null = null,
  conversationActorRole?: ConversationActorRole
) => {
  trackGTMEvent({
    event: 'ViewConversation',
    conversationId,
    listing: listing ?? undefined,
    details: details ?? undefined,
    listingAdvertiserType: getListingAdvertiserType(listing),
    conversationActorRole,
  })
}

export const trackMessagePost = (
  conversationId: number,
  listing: Listing | null,
  listingsCount: number,
  listingLinksCount: number,
  conversationActorRole?: ConversationActorRole
) => {
  trackGTMEvent({
    event: 'SendMessage',
    conversationId,
    listing: listing ?? undefined,
    listingAdvertiserType: getListingAdvertiserType(listing),
    conversationActorRole,
    details: {
      listingsCount,
      listingLinksCount,
    },
  })
}

export const trackConversationJoin = (
  conversationId: number,
  listing: Listing | null,
  conversationActorRole?: ConversationActorRole
) => {
  trackGTMEvent({
    event: 'JoinConversation',
    conversationId,
    listing: listing ?? undefined,
    conversationActorRole,
  })
}

export const trackViewEmail = (
  conversationId: number,
  listing: Listing | null,
  conversationActorRole?: ConversationActorRole
) => {
  trackGTMEvent({
    event: 'OpenReplyByEmailModal',
    conversationId,
    listing: listing ?? undefined,
    conversationActorRole,
  })
}

export const launchHubSpotChat = () => {
  trackGTMEvent({ event: 'launchChat' })
}

export const identifyUserInHubspot = () => {
  trackGTMEvent({ event: 'identifyUser' })
}

export const trackOpenParticipantDetails = (
  conversationActorRole?: ConversationActorRole
) => {
  trackGTMEvent({ event: 'ViewParticipantDetails', conversationActorRole })
}

export const trackDownloadAppModalOpened = () => {
  trackGTMEvent({ event: 'DownloadAppModalOpened' })
}

export const trackUnlockContactDetailsFromMessages = (
  conversationId?: number,
  listing?: Listing | null,
  conversationActorRole?: ConversationActorRole
) => {
  trackGTMEvent({
    event: 'UnlockContactDetailsFromMessages',
    conversationId,
    listing: listing ?? undefined,
    conversationActorRole,
  })
}

export const trackCallBuyerFromMessages = (
  conversationId?: number,
  listing?: Listing | null,
  conversationActorRole?: ConversationActorRole
) => {
  trackGTMEvent({
    event: 'CallBuyerFromMessages',
    conversationId,
    listing: listing ?? undefined,
    conversationActorRole,
  })
}

export const trackViewProfileFromMessages = (
  conversationId?: number,
  listing?: Listing | null,
  conversationActorRole?: ConversationActorRole
) => {
  trackGTMEvent({
    event: 'ViewProfileFromMessages',
    conversationId,
    listing: listing ?? undefined,
    conversationActorRole,
  })
}

export const trackCallAgentfromMessages = (
  conversationId?: number,
  listing?: Listing | null,
  conversationActorRole?: ConversationActorRole
) => {
  trackGTMEvent({
    event: 'CallAgentFromMessages',
    conversationId,
    listing: listing ?? undefined,
    conversationActorRole,
  })
}

export const trackFollowUpSendReminder = (
  conversationId: number,
  listing: Listing | null,
  conversationActorRole?: ConversationActorRole
) => {
  trackGTMEvent({
    event: 'FollowUpSendReminder',
    conversationId,
    listing: listing ?? undefined,
    conversationActorRole,
  })
}

export const trackFollowUpSendReminderClick = (
  conversationId: number,
  listing: Listing | null,
  conversationActorRole?: ConversationActorRole
) => {
  trackGTMEvent({
    event: 'FollowUpSendReminderClick',
    conversationId,
    listing: listing ?? undefined,
    conversationActorRole,
  })
}

export const trackFollowUpCallContact = (
  conversationId: number,
  listing: Listing | null,
  conversationActorRole?: ConversationActorRole
) => {
  trackGTMEvent({
    event: 'FollowUpCallContact',
    conversationId,
    listing: listing ?? undefined,
    conversationActorRole,
  })
}

export const trackFollowUpCallContactClick = (
  conversationId: number,
  listing: Listing | null,
  conversationActorRole?: ConversationActorRole
) => {
  trackGTMEvent({
    event: 'FollowUpCallContactClick',
    conversationId,
    listing: listing ?? undefined,
    conversationActorRole,
  })
}

export const trackFollowUpNotInterested = (
  conversationId: number,
  listing: Listing | null,
  conversationActorRole?: ConversationActorRole
) => {
  trackGTMEvent({
    event: 'FollowUpNotInterested',
    conversationId,
    listing: listing ?? undefined,
    conversationActorRole,
  })
}

export const trackFollowUpNotInterestedClick = (
  conversationId: number,
  listing: Listing | null,
  conversationActorRole?: ConversationActorRole
) => {
  trackGTMEvent({
    event: 'FollowUpNotInterestedClick',
    conversationId,
    listing: listing ?? undefined,
    conversationActorRole,
  })
}

export const trackReplySelectModalOpened = (
  conversationId: number,
  { listing, transactionType = null, sort }: ReplySelectModalOpenedDetails,
  conversationActorRole?: ConversationActorRole
) => {
  trackGTMEvent({
    event: 'ReplySelectModalOpened',
    conversationId,
    listing: listing ?? undefined,
    conversationActorRole,
    details: {
      transactionType:
        transactionType === null
          ? null
          : REPLY_SELECT_TRANSACTION_LABEL_MAP[transactionType] ??
            transactionType,
      sort: REPLY_SELECT_SORT_LABEL_MAP[sort] ?? sort,
      selectedListingId: null,
      selectedListingType: null,
    },
  })
}

export const trackReplySelectEntitySent = (
  conversationId: number,
  { selectedListingId, selectedListingType, listing }: ReplySelectEntityDetails,
  conversationActorRole?: ConversationActorRole
) => {
  trackGTMEvent({
    event: 'ReplySelectEntitySent',
    conversationId,
    listing: listing ?? undefined,
    conversationActorRole,
    details: { selectedListingId, selectedListingType },
  })
}

export const trackSavingPreferences = (
  languageExceptions: string[],
  added: string[],
  removed: string[]
) => {
  trackGTMEvent({
    event: 'SavingPreferences',
    details: { languageExceptions, added, removed },
  })
}

export const trackMarkMessageAsSafe = (
  conversationId: number,
  messageId: number,
  conversationActorRole?: ConversationActorRole
) => {
  trackGTMEvent({
    event: 'MarkMessageAsSafe',
    conversationId,
    messageId,
    conversationActorRole,
  })
}

export const trackMarkMessageAsSpam = (
  conversationId: number,
  messageId: number,
  conversationActorRole?: ConversationActorRole
) => {
  trackGTMEvent({
    event: 'MarkMessageAsSpam',
    conversationId,
    messageId,
    conversationActorRole,
  })
}

export const trackReplySelectSearchUsed = ({
  entityType,
}: ReplySelectSearchDetails,
conversationActorRole?: ConversationActorRole) => {
  trackGTMEvent({
    event: 'ReplySelectSearchUsed',
    conversationActorRole,
    details: { entityType },
  })
}

export const trackReplySelectTransactionTypeChanged = ({
  entityType,
  transactionType,
}: ReplySelectTransactionTypeDetails,
conversationActorRole?: ConversationActorRole) => {
  trackGTMEvent({
    event: 'ReplySelectTransactionTypeChanged',
    conversationActorRole,
    details: {
      entityType,
      transactionType:
        (transactionType &&
          REPLY_SELECT_TRANSACTION_LABEL_MAP[transactionType]) ??
        transactionType,
    },
  })
}

export const trackReplySelectSortingChanged = ({
  entityType,
  sort,
}: ReplySelectSortingDetails,
conversationActorRole?: ConversationActorRole) => {
  trackGTMEvent({
    event: 'ReplySelectSortingChanged',
    conversationActorRole,
    details: {
      entityType,
      sort: REPLY_SELECT_SORT_LABEL_MAP[sort] ?? sort,
    },
  })
}

export const trackChatFilterSelected = (filter: ChatFilterEventType) => {
  trackGTMEvent({
    event: 'ChatFilterSelected',
    details: { filter },
  })
}
